import {
  IBookingReceipt,
  IClosingReceipt,
  PrintTemplates,
  IBookingTicket,
  IBookingMiniTicket,
  IBookingLabel,
  LabelTypes,
  TicketTypes,
  IClosingSummaryReceipt,
} from "@/interfaces/receipts";
import {
  printCommonReceipt,
  printCommonTicket,
  printWithPrinter,
} from "@/utils/helpers/cross-platform";
import handlebars from "handlebars";
import {
  ticketTemplate,
  miniTicketTemplate,
  VHSTicketTemplate,
  JumparkLabelTemplate,
  FlipLabLabelTemplate,
} from "../templates/templates";
import QRCode from "qrcode";
import storageService, { LocalStorageKeys } from "@/services/storage.service";
import PrinterConfigModule from "@/store/modules/printers-config";
import POSConfigModule from "@/store/modules/pos-config";
import {
  EdeisSmallTicketTemplate,
  EdeisLargeTicketTemplate,
  PaulRicardTicketTemplate,
} from "../templates/ticket-templates";
import {
  ClosingReceiptTemplate,
  ClosingSummaryReceiptTemplate,
  orderSummaryReceipt,
  ReceiptTemplate,
} from "../templates/receipt-templates";
import {
  HeaderNamePartial,
  HeaderBusinessIdPartial,
  HeaderReprintLabelsPartial,
  ReceiptInfoCroatiaPartial,
  ReceiptInfoBelgiumPartial,
  ReceiptInfoFrancePartial,
  ReceiptInfoAustriaPartial,
  DateSellerRowPartial,
  SignatureFooterPartial,
} from "../templates/receipt-partials";
import Timeout from "await-timeout";
import { renderReceiptHtml, type ReceiptInput } from "@smeetz/receipt";
import { isUnifiedReceiptEnabled } from "@/utils/helpers/feature-flags";
import { LogService, LogLevel } from "@/services/logging";
import {
  getLocalPictureBase64,
  SmeetzPictureEntityTypes,
} from "@/utils/helpers/pictures";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;

// Replace the # character as it causes issues with the printer
handlebars.registerHelper("replaceHash", function (text: string) {
  return text.replace(/#/g, "-");
});

handlebars.registerHelper("eq", function (a, b) {
  return a === b;
});

handlebars.registerHelper("formatAmount", function (amountInCents) {
  if (amountInCents === undefined || amountInCents === null) return "0.00";
  return (Number(amountInCents) / 100).toFixed(2);
});

handlebars.registerHelper("t", function (key, options) {
  // pass options.hash if you need interpolation
  return t(key, options.hash || {});
});

// Register receipt partials for country-specific sections
handlebars.registerPartial("headerName", HeaderNamePartial);
handlebars.registerPartial("headerBusinessId", HeaderBusinessIdPartial);
handlebars.registerPartial("headerReprintLabels", HeaderReprintLabelsPartial);
handlebars.registerPartial("receiptInfoFrance", ReceiptInfoFrancePartial);
handlebars.registerPartial("receiptInfoAustria", ReceiptInfoAustriaPartial);
handlebars.registerPartial("receiptInfoCroatia", ReceiptInfoCroatiaPartial);
handlebars.registerPartial("receiptInfoBelgium", ReceiptInfoBelgiumPartial);
handlebars.registerPartial("dateSellerRow", DateSellerRowPartial);
handlebars.registerPartial("signatureFooter", SignatureFooterPartial);

/**
 * Renders the handlebars template source with data to string
 * @param source Template to render
 * @param data Render data
 * @returns rendered template as string
 */
function renderToString(source: string, data: unknown) {
  const template = handlebars.compile(source);
  const outputString = template(data);
  return outputString;
}

export function printReceipt(
  receiptInfo: IBookingReceipt,
  receiptInput?: ReceiptInput
) {
  // Legacy render — kept as the fallback and the parity baseline during rollout.
  handlebars.partials[PrintTemplates.BookingReceipt] = ReceiptTemplate;
  const legacyString = decodeHtml(
    renderToString(
      handlebars.partials[PrintTemplates.BookingReceipt],
      receiptInfo
    )
  );

  // Primary render — the shared @smeetz/receipt package. Rendered on EVERY print
  // (even flag off) so the two outputs can be compared; the Flagsmith
  // `pos::unified-receipt` flag decides what prints. Output is already decoded
  // and '#'-safe — do NOT pass through decodeHtml again.
  let packageString: string | undefined;
  let renderError: unknown;
  if (receiptInput) {
    try {
      packageString = renderReceiptHtml(receiptInput);
    } catch (err) {
      renderError = err;
      // ERROR level — the receipt silently fell back to legacy, which must be
      // visible in every build. Field-scoped: the error only, never the input.
      LogService.info(
        "Receipt: @smeetz/receipt render failed, falling back to legacy template",
        { err },
        LogLevel.Error
      );
    }
  }

  const flagOn = isUnifiedReceiptEnabled();
  const usePackage = flagOn && !!packageString;
  const receiptString = usePackage ? (packageString as string) : legacyString;

  // ROLLOUT PARITY LOG — NON-PRODUCTION builds only. Receipt HTML carries
  // customer/fiscal data, so none of this ships in production. Drop once the
  // package receipt is fully validated.
  if (import.meta.env.VITE_ENV !== "prod") {
    const source = usePackage
      ? "@smeetz/receipt PACKAGE"
      : `LEGACY template (${
          !receiptInput
            ? "no ReceiptInput built upstream"
            : renderError
            ? "package render THREW — see the error above"
            : "pos::unified-receipt flag disabled or Flagsmith not ready"
        })`;
    console.log(`%c[receipt] PRINTED FROM: ${source}`, "font-weight:bold");
    console.log(
      "[receipt] receipt session:",
      receiptInput?.orderSession
        ? `${receiptInput.orderSession.orderLines?.length ?? 0} lines / ` +
            `${receiptInput.orderSession.payments?.length ?? 0} payments / ` +
            `${receiptInput.orderSession.discounts?.length ?? 0} discounts`
        : "(none)"
    );
    console.log(
      "[receipt][NEW — @smeetz/receipt]:",
      packageString ?? "(not rendered)"
    );
    console.log("[receipt][OLD — legacy template]:", legacyString);

    (window as any).receiptNew = packageString;
    (window as any).receiptOld = legacyString;
    (window as any).printedReceipt = receiptString;
  }

  const printer = PrinterConfigModule.receiptPrinter;
  if (printer) {
    printWithPrinter(printer, receiptString, 0);
  } else {
    printCommonReceipt(receiptString);
  }
  // handlebars.partials[PrintTemplates.BookingReceipt] = undefined;

  // check if we need to print the Order Summary Receipt
  if (
    PrinterConfigModule.isOrderSummaryReceiptEnabled &&
    PrinterConfigModule.orderSummaryReceiptPrinter
  ) {
    printOrderSummaryReceipt(
      receiptInfo,
      PrinterConfigModule.orderSummaryReceiptPrinter
    );
  }
}

export function printClosingReceipt(receiptInfo: IClosingReceipt) {
  handlebars.partials[PrintTemplates.BookingReceipt] = ClosingReceiptTemplate;
  const receiptString = renderToString(
    handlebars.partials[PrintTemplates.BookingReceipt],
    receiptInfo
  );
  console.log("decodeHtml receiptString");
  console.log(decodeHtml(receiptString));

  LogService.info(
    `(compressed) Sale closure (old): Closing receipt for cashfloat: ${receiptInfo.cf.value}`,
    {
      receipt: decodeHtml(receiptString),
    }
  );

  const printer = PrinterConfigModule.receiptPrinter;
  if (printer) {
    printWithPrinter(printer, decodeHtml(receiptString), 0);
  } else {
    printCommonReceipt(decodeHtml(receiptString));
  }
  // handlebars.partials[PrintTemplates.BookingReceipt] = undefined;
}

export function printClosingSummaryReceipt(
  receiptInfo: IClosingSummaryReceipt
) {
  handlebars.partials[PrintTemplates.ClosingSummaryReceipt] =
    ClosingSummaryReceiptTemplate;
  let receiptString = "";
  try {
    receiptString = renderToString(
      handlebars.partials[PrintTemplates.ClosingSummaryReceipt],
      receiptInfo
    );
  } catch (error) {
    console.error("Error rendering receipt template:", error);
    return;
  }
  console.log("decodeHtml receiptString");
  console.log(decodeHtml(receiptString));

  LogService.info(
    `(compressed) Sale closure: Closing summary receipt for cashfloat: ${receiptInfo.cashfloatUid.value}`,
    {
      receipt: decodeHtml(receiptString),
    }
  );

  const printer = PrinterConfigModule.receiptPrinter;
  if (printer) {
    printWithPrinter(printer, decodeHtml(receiptString), 0);
  } else {
    printCommonReceipt(decodeHtml(receiptString));
  }
}

export function decodeHtml(html: string) {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
}

export function printTicket(ticketInfo: IBookingTicket, printer: string) {
  console.log("----------- hi from printTicket");
  handlebars.partials[PrintTemplates.BookingTicket] = ticketTemplate;
  for (const ticket of ticketInfo.tickets) {
    const ticketString = renderToString(
      handlebars.partials[PrintTemplates.BookingTicket],
      ticket
    );

    printWithPrinter(printer, decodeHtml(ticketString), 1);
  }
}

export function printMiniTicket(ticketInfo: IBookingTicket) {
  console.log("-----------POS-430 hi from printMiniTicket");
  console.log("ticketInfo");
  console.log(ticketInfo);

  handlebars.partials[PrintTemplates.BookingMiniTicket] = miniTicketTemplate;

  const printer = PrinterConfigModule.receiptPrinter;
  const tickets: IBookingMiniTicket[] = [];
  for (const t of ticketInfo.tickets) {
    tickets.push({
      groupLogoBase64: ticketInfo.groupLogoBase64,
      groupInfo: ticketInfo.groupInfo,
      bookingRef: ticketInfo.bookingRef,
      currency: ticketInfo.currency,
      name: t.categoryName,
      productName: t.productName,
      priceName: t.priceName,
      priceValue: t.priceValue,
      startDateTime: t.startDateTime,
      endDateTime: t.endDateTime,
      printDateTime: t.printDateTime,
      productCustomText: t.productCustomText,
      subCategoryName: t.subCategoryName,
      qrCode: t.qrcode,
      seat: t.seat,
      fieldReference: ticketInfo.fieldReference,
      fieldTicket: ticketInfo.fieldTicket,
      fieldPaid: ticketInfo.fieldPaid,
      fieldFrom: ticketInfo.fieldFrom,
      fieldTo: ticketInfo.fieldTo,
      fieldPrice: ticketInfo.fieldPrice,
      fieldSeat: ticketInfo.fieldSeat,
      fieldPrintDateTime: ticketInfo.fieldPrintDateTime,
      voucherCode: t.voucherCode,
    });
  }
  console.log("printing tickets");
  console.log(tickets);
  for (const ticket of tickets) {
    const ticketString = renderToString(
      handlebars.partials[PrintTemplates.BookingMiniTicket],
      ticket
    );
    console.log(ticketString);

    if (printer) {
      printWithPrinter(printer, decodeHtml(ticketString), 0);
    } else {
      printCommonReceipt(decodeHtml(ticketString));
    }
  }
}

export function printLabel(labelInfo: IBookingLabel, labelType?: LabelTypes) {
  if (labelType === LabelTypes.Jumpark) {
    // use jumpark template
    handlebars.partials[PrintTemplates.BookingLabel] = JumparkLabelTemplate;
  } else {
    // use fliplab template
    handlebars.partials[PrintTemplates.BookingLabel] = FlipLabLabelTemplate;
  }
  const labelString = renderToString(
    handlebars.partials[PrintTemplates.BookingLabel],
    labelInfo
  );
  console.log(labelString);
  if (!PrinterConfigModule.labelPrinter) return;
  printWithPrinter(
    PrinterConfigModule.labelPrinter,
    decodeHtml(labelString),
    0
  );
}

export async function printNativeTicket(
  ticketInfo: IBookingTicket,
  printer: string
) {
  // select a template based on the group id and ticket type
  const ticketType = PrinterConfigModule.ticketType;
  if (POSConfigModule.isVHS) {
    handlebars.partials[PrintTemplates.BookingTicket] = VHSTicketTemplate;
  } else if (ticketType === TicketTypes.EdeisSmall) {
    handlebars.partials[PrintTemplates.BookingTicket] =
      EdeisSmallTicketTemplate;
  } else if (
    POSConfigModule.groupId === 19579 &&
    ticketType === TicketTypes.EdeisLarge
  ) {
    // Paul Ricard custom template
    handlebars.partials[PrintTemplates.BookingTicket] =
      PaulRicardTicketTemplate;
  } else {
    // Default to large ticket size for Edeis
    handlebars.partials[PrintTemplates.BookingTicket] =
      EdeisLargeTicketTemplate;
  }

  for (const t of ticketInfo.tickets) {
    const ticket: IBookingMiniTicket = {
      groupInfo: ticketInfo.groupInfo,
      bookingRef: ticketInfo.bookingRef,
      currency: ticketInfo.currency,
      name: t.categoryName,
      productName: t.productName,
      priceName: t.priceName,
      subCategoryName: t.subCategoryName,
      priceValue: t.priceValue,
      startDateTime: t.startDateTime,
      endDateTime: t.endDateTime,
      printDateTime: t.printDateTime,
      qrCode: t.qrcode,
      seat: t.seat,
      fieldReference: ticketInfo.fieldReference,
      fieldTicket: ticketInfo.fieldTicket,
      fieldPaid: ticketInfo.fieldPaid,
      fieldFrom: ticketInfo.fieldFrom,
      fieldTo: ticketInfo.fieldTo,
      fieldPrice: ticketInfo.fieldPrice,
      fieldSeat: ticketInfo.fieldSeat,
      voucherCode: t.voucherCode,
      productLogo: t.productLogo,
      fieldVHSTicketTax: ticketInfo.fieldVHSTicketTax,
      fieldVHSTicketTerms: ticketInfo.fieldVHSTicketTerms,
      isGiftCard: t.isGiftCard,
      isMembership: t.isMembership,
      multiDayTicket: t.multiDayTicket,
      fieldValidUntil: ticketInfo.fieldValidUntil,
      barCode: t.barCode,
    };
    const ticketString = renderToString(
      handlebars.partials[PrintTemplates.BookingTicket],
      ticket
    );
    console.log(ticketString);
    printWithPrinter(
      printer,
      decodeHtml(ticketString),
      POSConfigModule.isEdeis ? 1 : 0
    );
    await Timeout.set(200);
  }
}

export function printOrderSummaryReceipt(
  receiptInfo: IBookingReceipt,
  printer: string
) {
  handlebars.partials[PrintTemplates.OrderSummaryReceipt] = orderSummaryReceipt;
  const receiptString = renderToString(
    handlebars.partials[PrintTemplates.OrderSummaryReceipt],
    receiptInfo
  );
  console.log("decodeHtml receiptString");
  console.log(decodeHtml(receiptString));
  if (printer) {
    printWithPrinter(printer, decodeHtml(receiptString), 0);
  } else {
    printCommonReceipt(decodeHtml(receiptString));
  }
}
